import React from 'react';
import { Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  WalletOutlined,
  SendOutlined,
  ScanOutlined,
  CalendarOutlined,
  TagOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

const { Panel } = Collapse;

const iconMap = [
  <WalletOutlined style={{ color: '#005fcc', fontSize: 24 }} />,
  <SendOutlined style={{ color: '#005fcc', fontSize: 24 }} />,
  <ScanOutlined style={{ color: '#005fcc', fontSize: 24 }} />,
  <CalendarOutlined style={{ color: '#005fcc', fontSize: 24 }} />,
  <TagOutlined style={{ color: '#005fcc', fontSize: 24 }} />,
  <ShoppingCartOutlined style={{ color: '#005fcc', fontSize: 24 }} />,
  <SwapOutlined style={{ color: '#005fcc', fontSize: 24 }} />,
  <ThunderboltOutlined style={{ color: '#005fcc', fontSize: 24 }} />,
];

interface UseCase {
  description: string;
  title: string;
}

interface Country {
  flag: string;
  key: string;
  label: string;
  useCases: UseCase[];
}

export const CountryUseCasesSection = () => {
  const { t } = useTranslation();

  const argentinaUseCases = t('features.countrySolutions.useCases.argentina', {
    returnObjects: true,
  }) as UseCase[];

  const countries: Country[] = [
    {
      flag: '\u{1F1E6}\u{1F1F7}',
      key: 'argentina',
      label: t('features.countrySolutions.countries.argentina'),
      useCases: argentinaUseCases,
    },
    {
      flag: '\u{1F1E7}\u{1F1F7}',
      key: 'brazil',
      label: t('features.countrySolutions.countries.brazil'),
      useCases: [],
    },
    {
      flag: '\u{1F1E8}\u{1F1F4}',
      key: 'colombia',
      label: t('features.countrySolutions.countries.colombia'),
      useCases: [],
    },
    {
      flag: '\u{1F1F2}\u{1F1FD}',
      key: 'mexico',
      label: t('features.countrySolutions.countries.mexico'),
      useCases: [],
    },
  ];

  const futureBadge = t('features.countrySolutions.futureBadge');

  return (
    <section id="features" className="w-full max-w-[1204px] mx-auto px-6">
      <div className="text-center mb-6">
        <p className="text-xs font-semibold text-blue-600 tracking-widest uppercase">
          {t('features.countrySolutions.label')}
        </p>
        <h2 className="text-[24px] sm:text-[40px] leading-[28px] sm:leading-[48px] font-bold text-black">
          {t('features.countrySolutions.title')}
        </h2>
      </div>

      <Collapse
        ghost
        defaultActiveKey={['argentina']}
        expandIconPosition="end"
        className="[&>.ant-collapse-item]:bg-[#f8fafc] [&>.ant-collapse-item]:rounded-xl [&>.ant-collapse-item]:mb-3 [&>.ant-collapse-item]:border-none [&>.ant-collapse-item>.ant-collapse-header]:!px-3 [&>.ant-collapse-item>.ant-collapse-content>.ant-collapse-content-box]:!px-3 [&>.ant-collapse-item>.ant-collapse-content]:!bg-transparent"
      >
        {countries.map((country) => (
          <Panel
            key={country.key}
            collapsible={country.useCases.length === 0 ? 'disabled' : undefined}
            header={
              <div className="flex items-center justify-between py-2.5">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{country.flag}</span>
                  <span
                    className={`text-lg font-semibold ${
                      country.useCases.length === 0
                        ? 'text-gray-400'
                        : 'text-black'
                    }`}
                  >
                    {country.label}
                  </span>
                </div>
                {country.useCases.length === 0 && (
                  <span className="text-xs font-medium text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                    {futureBadge}
                  </span>
                )}
              </div>
            }
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 pb-4">
              {country.useCases.map((useCase, index) => (
                <div
                  key={useCase.title}
                  className="bg-[#EDF6FB] rounded-2xl p-4 flex flex-col gap-2"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#f1f5fe] rounded-xl flex items-center justify-center shrink-0">
                      {iconMap[index]}
                    </div>
                    <h4 className="font-semibold text-black text-base">
                      {useCase.title}
                    </h4>
                  </div>
                  <p className="text-sm text-[#727272] leading-relaxed">
                    {useCase.description}
                  </p>
                </div>
              ))}
            </div>
          </Panel>
        ))}
      </Collapse>
    </section>
  );
};
